package com.merkost.handpass.admin.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.web.attributes.SelectAttrsScope
import com.merkost.handpass.admin.components.AdminButton
import com.merkost.handpass.admin.components.AdminButtonSize
import com.merkost.handpass.admin.components.AdminButtonVariant
import com.merkost.handpass.admin.components.AdminInput
import com.merkost.handpass.admin.components.AdminPageHeader
import com.merkost.handpass.admin.components.AdminPageScaffold
import com.merkost.handpass.admin.components.AdminPanel
import com.merkost.handpass.admin.components.AdminPageScope
import com.merkost.handpass.admin.components.AdminSearchInput
import com.merkost.handpass.admin.components.AdminTextArea
import com.merkost.handpass.admin.container.PartnerListingsContainer
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.PartnerListingsAction
import com.merkost.handpass.admin.contract.PartnerListingsIntent
import com.merkost.handpass.admin.contract.PartnerListingsState
import com.merkost.handpass.admin.contract.ToastKind
import com.merkost.handpass.admin.di.koinContainer
import com.merkost.handpass.api.dto.admin.AdminUserListItem
import com.merkost.handpass.api.dto.listing.CategoryResponse
import com.merkost.handpass.api.enums.Condition
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.Surface
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/partner-listings")
@Composable
fun PartnerListingsPage() {
    AdminPageScaffold(title = "Partner Listings", currentPath = "/partner-listings", showTitle = false) {
        val container = koinContainer<PartnerListingsContainer>()

        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is PartnerListingsAction.ShowSuccess -> toast(action.message, ToastKind.Success)
                    is PartnerListingsAction.ShowError -> toast(action.message, ToastKind.Error)
                }
            }
            var title by remember(state.selectedSeller?.id) { mutableStateOf("") }
            var description by remember(state.selectedSeller?.id) { mutableStateOf("") }
            var priceDollars by remember(state.selectedSeller?.id) { mutableStateOf("") }
            var categoryId by remember(state.selectedSeller?.id) { mutableStateOf("") }
            var condition by remember(state.selectedSeller?.id) { mutableStateOf(Condition.LIKE_NEW) }
            var city by remember(state.selectedSeller?.id) { mutableStateOf("") }
            var latitude by remember(state.selectedSeller?.id) { mutableStateOf("") }
            var longitude by remember(state.selectedSeller?.id) { mutableStateOf("") }

            PartnerListingsContent(
                state = state,
                title = title,
                description = description,
                priceDollars = priceDollars,
                city = city,
                latitude = latitude,
                longitude = longitude,
                categoryId = categoryId,
                condition = condition,
                onTitleChange = { title = it },
                onDescriptionChange = { description = it },
                onPriceChange = { priceDollars = it },
                onCityChange = { city = it },
                onLatitudeChange = { latitude = it },
                onLongitudeChange = { longitude = it },
                onCategoryChange = { categoryId = it },
                onConditionChange = { condition = it },
                onIntent = ::intent,
            )
        }
    }
}

@Composable
private fun AdminPageScope.PartnerListingsContent(
    state: PartnerListingsState,
    title: String,
    description: String,
    priceDollars: String,
    city: String,
    latitude: String,
    longitude: String,
    categoryId: String,
    condition: Condition,
    onTitleChange: (String) -> Unit,
    onDescriptionChange: (String) -> Unit,
    onPriceChange: (String) -> Unit,
    onCityChange: (String) -> Unit,
    onLatitudeChange: (String) -> Unit,
    onLongitudeChange: (String) -> Unit,
    onCategoryChange: (String) -> Unit,
    onConditionChange: (Condition) -> Unit,
    onIntent: (PartnerListingsIntent) -> Unit,
) {
    AdminPageHeader(
        title = "Create Partner Listing",
        subtitle = "Seed the marketplace with a real listing attributed to an existing seller",
    )

    AdminPanel(title = "Seller") {
        if (state.selectedSeller == null) {
            AdminSearchInput(
                initial = state.sellerQuery,
                onDebouncedChange = { onIntent(PartnerListingsIntent.SetSellerQuery(it)) },
                placeholder = "Search sellers by name or email…",
                widthPx = 360,
            )
            if (state.sellerSearchLoading) {
                Span({ style { color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Caption) } }) {
                    Text("Searching…")
                }
            }
            if (state.sellerResults.isNotEmpty()) {
                Div({ style { property("margin-top", "${HandpassSpacing.MD}") } }) {
                    state.sellerResults.forEach { seller ->
                        SellerResultRow(seller = seller, onClick = { onIntent(PartnerListingsIntent.SelectSeller(seller)) })
                    }
                }
            } else if (state.sellerQuery.isNotBlank() && !state.sellerSearchLoading) {
                Span({ style { color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Caption) } }) {
                    Text("No sellers found")
                }
            }
        } else {
            SelectedSellerRow(
                seller = state.selectedSeller,
                onChange = { onIntent(PartnerListingsIntent.SelectSeller(null)) },
            )
        }
    }

    if (state.selectedSeller != null) {
        Div({ style { property("margin-top", "${HandpassSpacing.XL}") } }) {
            AdminPanel(title = "Listing details") {
                ListingFormField(label = "Title") {
                    AdminInput(value = title, onChange = onTitleChange, placeholder = "What are you selling?")
                }
                ListingFormField(label = "Description") {
                    AdminTextArea(value = description, onChange = onDescriptionChange, placeholder = "Add useful details for buyers…")
                }
                ListingFormField(label = "Price (AUD, \$)") {
                    AdminInput(value = priceDollars, onChange = onPriceChange, placeholder = "0.00")
                }
                ListingFormField(label = "City") {
                    AdminInput(value = city, onChange = onCityChange, placeholder = "Sydney")
                }
                ListingFormField(label = "Latitude") {
                    AdminInput(value = latitude, onChange = onLatitudeChange, placeholder = "-33.8688")
                }
                ListingFormField(label = "Longitude") {
                    AdminInput(value = longitude, onChange = onLongitudeChange, placeholder = "151.2093")
                }
                ListingFormField(label = "Category") {
                    CategorySelect(state.categories, categoryId, onCategoryChange)
                }
                ListingFormField(label = "Condition") {
                    ConditionSelect(condition, onConditionChange)
                }
                ListingFormField(label = "Images") {
                    ImagePicker(state = state, onIntent = onIntent)
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        property("margin-top", "${HandpassSpacing.MD}")
                    }
                }) {
                    AdminButton(
                        label = "Create Listing",
                        variant = AdminButtonVariant.Primary,
                        enabled = !state.isSubmitting &&
                            title.trim().length in 3..200 &&
                            priceDollars.toDoubleOrNull()?.let { it > 0 } == true &&
                            categoryId.isNotBlank() &&
                            city.isNotBlank() &&
                            latitude.toDoubleOrNull()?.let { it in -90.0..90.0 } == true &&
                            longitude.toDoubleOrNull()?.let { it in -180.0..180.0 } == true &&
                            state.uploadedImageUrls.isNotEmpty(),
                        loading = state.isSubmitting,
                        onClick = {
                            onIntent(
                                PartnerListingsIntent.Submit(
                                    title = title,
                                    description = description,
                                    priceDollars = priceDollars,
                                    categoryId = categoryId,
                                    condition = condition,
                                    city = city,
                                    latitude = latitude,
                                    longitude = longitude,
                                ),
                            )
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun SellerResultRow(seller: AdminUserListItem, onClick: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("gap", "${HandpassSpacing.XXS}")
            property("padding", "${HandpassSpacing.SM} 0")
            property("border-bottom", "1px solid ${Border.value()}")
            property("cursor", "pointer")
        }
        onClick { onClick() }
    }) {
        Span({ style { color(OnSurface.value()); fontSize(HandpassTypography.Body); fontWeight(600) } }) {
            Text(seller.displayName)
        }
        Span({ style { color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Caption) } }) {
            Text(seller.email)
        }
    }
}

@Composable
private fun SelectedSellerRow(seller: AdminUserListItem, onChange: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            gap(HandpassSpacing.MD)
        }
    }) {
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); property("gap", "${HandpassSpacing.XXS}") } }) {
            Span({ style { color(OnSurface.value()); fontSize(HandpassTypography.Body); fontWeight(600) } }) { Text(seller.displayName) }
            Span({ style { color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Caption) } }) { Text(seller.email) }
        }
        AdminButton(label = "Change", onClick = onChange, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
    }
}

@Composable
private fun ListingFormField(label: String, content: @Composable () -> Unit) {
    Div({ style { property("margin-bottom", "${HandpassSpacing.MD}") } }) {
        Span({ style { display(DisplayStyle.Block); color(OnSurface.value()); fontSize(HandpassTypography.Caption); fontWeight(600); property("margin-bottom", "${HandpassSpacing.XS}") } }) {
            Text(label)
        }
        content()
    }
}

@Composable
private fun CategorySelect(
    categories: AsyncSection<List<CategoryResponse>>,
    selectedId: String,
    onChange: (String) -> Unit,
) {
    when (categories) {
        AsyncSection.Loading -> Select(attrs = {
            attr("disabled", "")
            selectStyle()
        }) { Option("") { Text("Loading categories…") } }
        is AsyncSection.Failed -> Span({ style { color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Caption) } }) {
            Text(categories.message)
        }
        is AsyncSection.Loaded -> {
            val flatCategories = remember(categories.value) { flattenCategories(categories.value) }
            Select(attrs = {
                attr("value", selectedId)
                selectStyle()
                onChange { onChange(it.value.orEmpty()) }
            }) {
                Option("", attrs = { if (selectedId.isBlank()) attr("selected", "") }) { Text("Select a category") }
                flatCategories.forEach { (depth, category) ->
                    Option(category.id, attrs = { if (category.id == selectedId) attr("selected", "") }) {
                        Text("${"— ".repeat(depth)}${category.name}")
                    }
                }
            }
        }
    }
}

@Composable
private fun ConditionSelect(condition: Condition, onChange: (Condition) -> Unit) {
    Select(attrs = {
        attr("value", condition.value)
        selectStyle()
        onChange { event -> onChange(Condition.fromString(event.value.orEmpty())) }
    }) {
        Option(Condition.NEW.value, attrs = { if (condition == Condition.NEW) attr("selected", "") }) { Text("New") }
        Option(Condition.LIKE_NEW.value, attrs = { if (condition == Condition.LIKE_NEW) attr("selected", "") }) { Text("Like New") }
        Option(Condition.USED.value, attrs = { if (condition == Condition.USED) attr("selected", "") }) { Text("Used") }
    }
}

private fun SelectAttrsScope.selectStyle() {
    style {
        property("width", "100%")
        property("padding", "${HandpassSpacing.MD} ${HandpassSpacing.Default}")
        property("box-sizing", "border-box")
        property("font-family", "inherit")
        property("font-size", HandpassTypography.Body.toString())
        property("color", OnSurface.value().toString())
        property("background-color", Surface.value().toString())
        property("border", "1px solid ${Border.value()}")
        property("border-radius", RadiusControl.value().toString())
    }
}

private fun flattenCategories(
    categories: List<CategoryResponse>,
    depth: Int = 0,
): List<Pair<Int, CategoryResponse>> = categories.flatMap { category ->
    listOf(depth to category) + flattenCategories(category.children.orEmpty(), depth + 1)
}

@Composable
private fun ImagePicker(state: PartnerListingsState, onIntent: (PartnerListingsIntent) -> Unit) {
    Input(type = InputType.File, attrs = {
        attr("accept", "image/*")
        attr("multiple", "")
        if (state.isUploadingImage) attr("disabled", "")
        onChange {
            val files = (it.target as HTMLInputElement).files
            if (files != null) {
                val fileCount = minOf(files.length, (10 - state.uploadedImageUrls.size).coerceAtLeast(0))
                for (index in 0 until fileCount) {
                    files.item(index)?.let { file -> onIntent(PartnerListingsIntent.UploadImage(file)) }
                }
            }
        }
    })
    if (state.isUploadingImage) {
        Span({ style { display(DisplayStyle.Block); color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Caption); property("margin-top", "${HandpassSpacing.SM}") } }) {
            Text("Uploading image…")
        }
    }
    if (state.uploadedImageUrls.isNotEmpty()) {
        Div({
            style {
                display(DisplayStyle.Flex)
                property("flex-wrap", "wrap")
                property("gap", "${HandpassSpacing.SM}")
                property("margin-top", "${HandpassSpacing.MD}")
            }
        }) {
            state.uploadedImageUrls.forEach { url ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(HandpassSpacing.XS)
                        property("padding", "${HandpassSpacing.XS}")
                        property("border", "1px solid ${Border.value()}")
                        property("border-radius", RadiusControl.value().toString())
                    }
                }) {
                    Img(src = url, attrs = {
                        attr("alt", "Listing image")
                        style {
                            width(48.px)
                            height(48.px)
                            property("object-fit", "cover")
                            property("border-radius", RadiusControl.value().toString())
                        }
                    })
                    Button(attrs = {
                        attr("type", "button")
                        attr("aria-label", "Remove image")
                        style {
                            property("border", "none")
                            property("background", "transparent")
                            property("color", OnSurfaceMuted.value().toString())
                            property("cursor", "pointer")
                            property("font-size", "16px")
                        }
                        onClick { onIntent(PartnerListingsIntent.RemoveImage(url)) }
                    }) { Text("✕") }
                }
            }
        }
    }
}
